import type { ToolBadgeSpec } from '../types.js';

import { readArray, readField, readString, truncateForLabel } from '../extractors.js';

/**
 * Badge for the Hotels Agent (`fetchCuratedHotels`).
 *
 * Unlike `fetchHotelData`, every hotel here is a deliberate pick with a reason, so the
 * dropdown always earns its place — there is no strategy to branch on and nothing in the list
 * that the client did not ask to see.
 *
 * The subtitle carries the one fact a hotel card does not show: what it costs. A plain figure is a
 * live rate Elara can book; `price.elaraTeamBooksDirect` means the hotel published it and the team
 * would book direct, so it is prefixed with `~` and never reads as a quote. Rates are per night
 * (see TRAVELPORT_LEAD_RATE_BASIS in server/src/hotel-curation/lead-rates.ts).
 *
 * Note that for hotel rows this title and subtitle are usually REPLACED: `row-enrichers/hotel.tsx`
 * loads the hotel document and prefers its name, address and brand logo. What is computed here is
 * the fallback for when that document does not load.
 */

/** Unique destination names across the presented hotels, for the "in London" label suffix. */
function destinationSummary(toolCall: { output?: unknown }): string | undefined {
  const hotels = readArray(readField(toolCall.output, 'hotels'));
  if (!hotels) return undefined;
  const seen = new Set<string>();
  for (const hotel of hotels) {
    const name = readString(readField(readField(hotel, 'destination'), 'name'));
    if (name) seen.add(name);
  }
  return seen.size > 0 ? truncateForLabel([...seen].join(', ')) : undefined;
}

/** The nightly rate as a subtitle fragment, or nothing when there is no figure worth showing. */
function priceFragment(hotel: unknown): string | undefined {
  const price = readField(hotel, 'price');
  const status = readString(readField(price, 'status'));
  if (status === 'unavailable') return 'No availability for these dates';
  const rate = readField(price, 'nightly');
  const amount = readField(rate, 'amount');
  const currency = readString(readField(rate, 'currency'));
  if (typeof amount !== 'number' || !currency) return undefined;
  const rounded = Math.round(amount).toLocaleString('en-US');
  // The tilde marks a figure the hotel published rather than an Elara quote.
  return readField(price, 'elaraTeamBooksDirect') ? `~${currency} ${rounded} a night` : `${currency} ${rounded} a night`;
}

export const fetchCuratedHotelsSpec: ToolBadgeSpec = {
  toolName: 'fetchCuratedHotels',
  icon: 'hotel',
  label: (toolCall, active) => {
    const base = active ? 'Searching for hotels' : 'Searched for hotels';
    const location = destinationSummary(toolCall);
    return location ? `${base} in ${location}` : base;
  },
  activitySummary: { label: (_, active) => (active ? 'Searching for hotels' : 'Searched for hotels') },
  resultNoun: { singular: 'hotel', plural: 'hotels' },
  dataSource: ['Elara collection', 'Travelport', 'Google Hotels'],
  extractResults: (toolCall) => {
    const hotels = readArray(readField(toolCall.output, 'hotels'));
    if (!hotels) return undefined;
    const items = [];
    for (const [index, hotel] of hotels.entries()) {
      const id = readString(readField(hotel, '_id'));
      if (!id) continue;
      const destination = readField(hotel, 'destination');
      const subtitle = [readString(readField(destination, 'name')), priceFragment(hotel)]
        .filter((part): part is string => !!part)
        .join(' · ');
      items.push({
        key: `${id}-${index}`,
        title: readString(readField(hotel, 'name')) ?? `Hotel ${index + 1}`,
        subtitle: subtitle || undefined,
        previewType: 'hotel' as const,
        id,
      });
    }
    if (items.length === 0) return undefined;
    const location = destinationSummary(toolCall);
    return { items, contextLabel: location ? `in ${location}` : undefined };
  },
};
